import React, { useEffect, useState } from 'react';
import { Loader2, Wrench } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';

const br = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '—');

// Títulos cuja parcela (rótulo) não corresponde ao mês/ano do vencimento. Corrige só o rótulo.
export function ParcelasDivergentesDialog({ open, onOpenChange, onFixed }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => {
    setRows(null);
    api.get('/student-contracts/maintenance/parcelas-divergentes')
      .then(({ data }) => setRows(data.titulos || [])).catch(() => { setRows([]); toast.error('Falha ao carregar'); });
  };
  useEffect(() => { if (open) load(); }, [open]);

  const fix = async () => {
    if (!window.confirm(`Corrigir o rótulo da parcela de ${rows.length} título(s)? Valores, vencimentos, pagamentos e boletos não são alterados.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post('/student-contracts/maintenance/parcelas-divergentes/corrigir', { ids: rows.map((r) => r.id) });
      toast.success(`${data.corrigidos} parcela(s) corrigida(s)`);
      load();
      onFixed?.();
    } catch { toast.error('Falha ao corrigir'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="parcelas-divergentes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Wrench className="h-4 w-4 text-violet-600" /> Parcelas com mês/ano diferente do vencimento</DialogTitle>
          <DialogDescription>A parcela passa a mostrar MM/AAAA do próprio vencimento. O rótulo anterior fica guardado no título.</DialogDescription>
        </DialogHeader>
        {!rows ? <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Carregando…</p> : (
          <>
            <p className="text-sm font-semibold" data-testid="parcelas-divergentes-total">{rows.length} título(s) encontrados</p>
            <div className="max-h-[55vh] overflow-auto rounded-lg border border-slate-200">
              <table className="w-full text-[12px]">
                <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase text-slate-500">
                  <tr><th className="px-2 py-1 text-left">Aluno</th><th className="text-left">Descrição</th><th className="text-left">Parcela atual</th><th className="text-left">Correta</th><th className="text-left">Vencimento</th><th className="px-2 text-left">Status</th></tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100" data-testid={`parcela-div-${r.id}`}>
                      <td className="px-2 py-1">{r.aluno || '—'}</td><td>{r.descricao || '—'}</td>
                      <td className="text-rose-700">{r.parcela_atual ?? '(vazio)'}</td><td className="font-bold text-emerald-700">{r.parcela_correta}</td>
                      <td>{br(r.vencimento)}</td><td className="px-2">{r.status}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && <tr><td colSpan={6} className="py-4 text-center text-slate-400">Nenhuma parcela divergente.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Fechar</Button>
              <Button onClick={fix} disabled={busy || rows.length === 0} className="bg-violet-600 hover:bg-violet-700" data-testid="parcelas-divergentes-corrigir">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Corrigir ${rows.length} parcela(s)`}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
